import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeading from "./SectionHeading";
import { SOCIAL_SLIDER_IMAGES, SOCIAL_GRID_CARDS } from "../data/siteContent";

function sliderIndexForFile(file) {
  return SOCIAL_SLIDER_IMAGES.findIndex((slide) => slide.file === file);
}

export default function SocialShowcase() {
  const [sliderIdx, setSliderIdx] = useState(0);
  const [activeGrid, setActiveGrid] = useState(null);

  const goToFile = useCallback((file, gridIndex = null) => {
    const i = sliderIndexForFile(file);
    if (i >= 0) setSliderIdx(i);
    setActiveGrid(gridIndex);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setSliderIdx((i) => (i + 1) % SOCIAL_SLIDER_IMAGES.length);
      setActiveGrid(null);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const currentSlide = SOCIAL_SLIDER_IMAGES[sliderIdx];

  return (
    <section className="relative overflow-hidden bg-ink-soft py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Social & Ads"
          title="Content engineered for"
          titleAccent="engagement"
          subtitle="Instagram grids, Facebook ads, branding, and campaign visuals — all under one creative roof."
        />
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative aspect-square w-full overflow-hidden rounded-3xl border border-white/10"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={currentSlide.file}
                src={currentSlide.image}
                alt={currentSlide.label}
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6 }}
              />
            </AnimatePresence>
            <div className="absolute bottom-4 left-4 rounded-full bg-ink/80 px-4 py-2 text-sm font-semibold text-white backdrop-blur">
              {activeGrid !== null
                ? SOCIAL_GRID_CARDS[activeGrid].platform
                : currentSlide.label}
            </div>
            <div className="absolute bottom-4 right-4 flex gap-1.5">
              {SOCIAL_SLIDER_IMAGES.map((slide, i) => (
                <button
                  key={slide.file}
                  type="button"
                  aria-label={`Show ${slide.label}`}
                  onClick={() => goToFile(slide.file)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === sliderIdx ? "w-6 bg-accent" : "w-1.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            {SOCIAL_GRID_CARDS.map((card, i) => {
              const isActive = activeGrid === i || (activeGrid === null && sliderIdx === sliderIndexForFile(card.file));
              return (
                <motion.button
                  key={card.file}
                  type="button"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => goToFile(card.file, i)}
                  className={`group relative aspect-square w-full overflow-hidden rounded-2xl border transition ${
                    isActive
                      ? "border-accent ring-2 ring-accent/40"
                      : "border-white/10 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={card.image} alt={card.platform} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(2,6,23,0.75)_0%,rgba(2,6,23,0.35)_35%,transparent_70%)]" />
                  <p className="absolute inset-x-0 bottom-0 px-3 py-3 text-center text-xs font-semibold text-white">
                    {card.platform}
                  </p>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
